import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CabPage from "../CabPage";
import "../css/editvehicle.css";
import useFleet, { BODY_TYPE_OPTIONS, STATUS_META, currencySign, plural } from "../fleet/useFleet";
import { Ic, VehiclePhoto, StatusPill, MobileNav, Metric, METRIC_ICONS, ICONS, Dialog, useToast, NoFleetAccess } from "../fleet/ui";
import { VIN_MSG, VIN_RE, apiError, checkDocFile, getTransport, patchTransport, readFile, shortName } from "../fleet/api";

const SORTERS = {
  vehicle: (v) => `${v.mark} ${v.model}`.toLowerCase(),
  plate: (v) => v.plate.toLowerCase(),
  city: (v) => (v.city || "").toLowerCase(),
  driver: (v) => (v.driver_name || "").toLowerCase(),
  rate: (v) => v.min_price ?? -1,
};

// document rows of the panel: wireframe «Техпаспорт», «Страховка» (+ licence from the Add page and the vehicle photo)
const DOCS = [
  { key: "passport", title: "Техпаспорт", field: "tech_passport_of_tractor", have: "passport_doc", done: "Загружен", none: "Не загружен" },
  { key: "insurance", title: "Страховка", field: "insurance_photo", have: "insurance_doc", done: "Загружена", none: "Не загружена" },
  { key: "licence", title: "Лицензия", field: "licence_photo", have: "licence_doc", done: "Загружена", none: "Не загружена" },
  { key: "photo", title: "Фото машины", field: "tractor_photo", have: "photo", done: "Загружено", none: "Не загружено" },
];

const EMPTY = { mark: "", model: "", plate: "", vin: "", body: "", capacity: "", volume: "", registration_city: "", driver_name: "", driver_phone: "", comment: "" };

const fromTransport = (t) => ({
  mark: t.mark || "",
  model: t.model || "",
  plate: t.government_number || "",
  vin: t.vin || "",
  body: t.body_type && typeof t.body_type === "object" ? String(t.body_type.id) : t.body_type ? String(t.body_type) : "",
  capacity: t.max_load_weight ?? "",
  volume: t.max_load_volume ?? "",
  registration_city: t.registration_city || "",
  driver_name: t.driver_name || "",
  driver_phone: t.driver_phone || "",
  comment: t.vehicle_comment || "",
});

export default function EditvehiclePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const vehicleId = Number(id);
  const { canFleet, loading, vehicles, stats, load } = useFleet();
  const [toast, toastNode] = useToast();

  const [tab, setTab] = useState("all");
  const [f, setF] = useState({ status: "all", body: "all", city: "all", driver: "all", query: "" });
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [checked, setChecked] = useState([]);

  // ── edit panel ─────────────────────────────────────────────
  const [panelOpen, setPanelOpen] = useState(true);
  const [detail, setDetail] = useState(null); // raw transport from API
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState({}); // staged uploads {field: dataURL, field+"__name": file name}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dlg, setDlg] = useState(null); // {doc}
  const fileRef = useRef(null);
  const pendingDoc = useRef(null);
  const vinRef = useRef(null);
  const panelRef = useRef(null);

  const fetchDetail = (vid) =>
    getTransport(vid).then((t) => {
      setDetail(t);
      setForm(fromTransport(t));
      return t;
    });

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setNotFound(false);
    setFiles({});
    setErr("");
    setPanelOpen(true);
    if (!vehicleId) return undefined;
    getTransport(vehicleId)
      .then((t) => {
        if (!alive) return;
        setDetail(t);
        setForm(fromTransport(t));
        if (panelRef.current) panelRef.current.scrollTop = 0;
      })
      .catch(() => alive && setNotFound(true));
    return () => {
      alive = false;
    };
  }, [vehicleId]);

  // wireframe: Escape closes the dialog (Dialog handles it) or else the edit panel
  const dlgOpen = useRef(false);
  dlgOpen.current = !!dlg;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !dlgOpen.current) setPanelOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const row = vehicles.find((v) => v.vehicle_id === vehicleId) || null;
  const mine = !loading && !!row;
  const setField = (k) => (e) => {
    if (k === "vin") e.target.setCustomValidity("");
    setForm((p) => ({ ...p, [k]: e.target.value }));
  };

  // wireframe `vehicle` action: load the clicked vehicle into the panel (discarding unsaved edits)
  const openVehicle = (vid) => {
    setPanelOpen(true);
    setErr("");
    setFiles({});
    if (vid === vehicleId) {
      if (detail) setForm(fromTransport(detail));
      if (panelRef.current) panelRef.current.scrollTop = 0;
    } else navigate(`/my-fleet/${vid}/edit`);
  };

  const cancel = () => {
    if (detail) setForm(fromTransport(detail));
    setFiles({});
    setErr("");
    vinRef.current?.setCustomValidity("");
    setPanelOpen(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    const vin = form.vin.trim();
    if (vin && !VIN_RE.test(vin)) {
      vinRef.current.setCustomValidity(VIN_MSG);
      vinRef.current.reportValidity();
      return;
    }
    setBusy(true);
    try {
      const body = {
        mark: form.mark.trim(),
        model: form.model.trim() || null,
        government_number: form.plate.trim(),
        vin: vin ? vin.toUpperCase() : null,
        body_type: form.body ? Number(form.body) : null,
        max_load_weight: form.capacity !== "" ? Number(form.capacity) : null,
        max_load_volume: form.volume !== "" ? Number(form.volume) : null,
        registration_city: form.registration_city.trim() || null,
        driver_name: form.driver_name.trim() || null,
        driver_phone: form.driver_phone.trim() || null,
        vehicle_comment: form.comment.trim() || null,
      };
      for (const d of DOCS) if (files[d.field]) body[d.field] = files[d.field];
      await patchTransport(vehicleId, body);
      setFiles({});
      await load();
      await fetchDetail(vehicleId);
      setPanelOpen(false);
      toast("Изменения сохранены");
    } catch (e2) {
      setErr(apiError(e2, "Не удалось сохранить"));
    } finally {
      setBusy(false);
    }
  };

  const pickFile = async (file) => {
    const doc = pendingDoc.current;
    if (!file || !doc) return;
    const bad = checkDocFile(file);
    if (bad) return toast(bad);
    try {
      const data = await readFile(file);
      setFiles((p) => ({ ...p, [doc.field]: data, [`${doc.field}__name`]: file.name }));
      toast(`Файл «${file.name}» выбран — нажмите «Сохранить изменения»`);
    } catch {
      toast("Не удалось прочитать файл");
    }
  };

  // ── table ──────────────────────────────────────────────────
  const setFilter = (patch) => {
    setF((p) => ({ ...p, ...patch }));
    setPage(1);
  };
  const bodyOptions = useMemo(() => {
    const m = new Map();
    vehicles.forEach((v) => v.body_type_id && m.set(v.body_type_id, v.body_type));
    return [...m.entries()];
  }, [vehicles]);
  const cityOptions = useMemo(() => [...new Set(vehicles.map((v) => v.city).filter(Boolean))], [vehicles]);
  const driverOptions = useMemo(() => [...new Set(vehicles.map((v) => v.driver_name).filter(Boolean))], [vehicles]);

  const filtered = useMemo(() => {
    const q = f.query.trim().toLowerCase();
    let list = vehicles.filter(
      (v) =>
        (tab !== "free" || v.state === "searching") &&
        (f.status === "all" || v.state === f.status) &&
        (f.body === "all" || String(v.body_type_id) === f.body) &&
        (f.city === "all" || v.city === f.city) &&
        (f.driver === "all" || v.driver_name === f.driver) &&
        (!q || `${v.mark} ${v.model} ${v.plate} ${v.driver_name} ${v.city || ""}`.toLowerCase().includes(q)),
    );
    if (sort.key) {
      const get = SORTERS[sort.key];
      list = [...list].sort((a, b) => {
        const x = get(a);
        const y = get(b);
        return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
      });
    }
    return list;
  }, [vehicles, f, tab, sort]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const curPage = Math.min(page, pages);
  const rows = filtered.slice((curPage - 1) * pageSize, curPage * pageSize);

  const allOnPage = rows.length > 0 && rows.every((v) => checked.includes(v.vehicle_id));
  const toggleAll = () =>
    setChecked((c) =>
      allOnPage ? c.filter((x) => !rows.some((v) => v.vehicle_id === x)) : [...new Set([...c, ...rows.map((v) => v.vehicle_id)])],
    );

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  const sortMark = (key) => (sort.key === key ? (sort.dir === 1 ? " ↑" : " ↓") : " ↕");
  const th = (key, text) => (
    <th onClick={() => toggleSort(key)} style={{ cursor: "pointer" }}>{text}{sortMark(key)}</th>
  );
  const resetFilters = () => {
    setF({ status: "all", body: "all", city: "all", driver: "all", query: "" });
    setTab("all");
    setPage(1);
  };
  const pickTab = (t) => {
    setTab(t);
    setPage(1);
  };

  const title = (v) => [v.mark, v.model && v.model !== v.mark ? v.model : ""].filter(Boolean).join(" ");
  const docOk = (d) => !!(files[d.field] || detail?.[d.field]);
  const docState = (d) => (files[d.field] ? "Выбран файл" : detail?.[d.field] ? d.done : d.none);

  const openDoc = (d) => {
    pendingDoc.current = d;
    setDlg({ doc: d });
  };
  const chooseDoc = () => {
    setDlg(null);
    fileRef.current?.click();
  };

  if (!canFleet) {
    return (
      <CabPage name="editvehicle">
        <NoFleetAccess />
        <MobileNav />
      </CabPage>
    );
  }

  const counter = `${form.comment.length}/500`;
  const dd = dlg?.doc;

  return (
    <CabPage name="editvehicle">
      <div className={panelOpen ? "" : "panel-closed"}>
        <main id="main">
          <section className="intro">
            <h1>Мой транспорт</h1>
            <p>Управляйте своим автопарком. Отмечайте свободные машины, получайте заказы и отслеживайте рейсы.</p>
          </section>

          <section className="fleet-metrics">
            <Metric idx={0} icon={METRIC_ICONS.total} value={stats.total} label="Всего машин" small="В автопарке" />
            <Metric idx={1} icon={METRIC_ICONS.free} value={stats.active} label="Ищут груз сейчас" />
            <Metric idx={2} icon={METRIC_ICONS.tomorrow} value={stats.tomorrow} label="Освобождаются завтра" />
            <Metric idx={3} icon={METRIC_ICONS.trip} value={stats.busy} label="В рейсе" />
          </section>

          <div className="fleet-tabs">
            <div aria-label="Раздел автопарка" role="tablist">
              <button aria-selected={tab === "all"} className={tab === "all" ? "active" : ""} onClick={() => pickTab("all")} role="tab" type="button">Автопарк</button>
              <button aria-selected={tab === "free"} className={tab === "free" ? "active" : ""} onClick={() => pickTab("free")} role="tab" type="button">
                Свободные машины <span>{stats.active}</span>
              </button>
              <button aria-selected="false" data-action="requests" onClick={() => navigate("/my-fleet")} role="tab" type="button">Запросы</button>
            </div>
            <button className="button primary add-vehicle" data-action="add" onClick={() => navigate("/my-fleet/add?new=1")} type="button">
              <Ic n="plus" />
              <span>Добавить машину</span>
            </button>
          </div>

          <form id="fleet-filters" onSubmit={(e) => e.preventDefault()}>
            <label className="field">
              <span>Статус</span>
              <span className="select-wrap">
                <select aria-label="Статус" onChange={(e) => setFilter({ status: e.target.value })} value={f.status}>
                  <option value="all">Все статусы</option>
                  <option value="searching">Ищет груз</option>
                  <option value="trip">В рейсе</option>
                  <option value="tomorrow">Освобождается завтра</option>
                  <option value="idle">Стоит</option>
                </select>
                <Ic n="chevron" />
              </span>
            </label>
            <label className="field">
              <span>Тип кузова</span>
              <span className="select-wrap">
                <select aria-label="Тип кузова" onChange={(e) => setFilter({ body: e.target.value })} value={f.body}>
                  <option value="all">Все типы</option>
                  {bodyOptions.map(([bid, name]) => <option key={bid} value={String(bid)}>{name}</option>)}
                </select>
                <Ic n="chevron" />
              </span>
            </label>
            <label className="field">
              <span>Город (где сейчас)</span>
              <span className="select-wrap">
                <select aria-label="Город (где сейчас)" onChange={(e) => setFilter({ city: e.target.value })} value={f.city}>
                  <option value="all">Все города</option>
                  {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Ic n="chevron" />
              </span>
            </label>
            <label className="field">
              <span>Водитель</span>
              <span className="select-wrap">
                <select aria-label="Водитель" onChange={(e) => setFilter({ driver: e.target.value })} value={f.driver}>
                  <option value="all">Все водители</option>
                  {driverOptions.map((d) => <option key={d} value={d}>{shortName(d)}</option>)}
                </select>
                <Ic n="chevron" />
              </span>
            </label>
            <label className="fleet-search">
              <Ic n="search" />
              <input aria-label="Поиск по госномеру или марке" onChange={(e) => setFilter({ query: e.target.value })} placeholder="Поиск по госномеру, марке..." value={f.query} />
            </label>
          </form>

          <div className="table-container">
            <table>
              <colgroup>
                <col style={{ width: "4%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10.5%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "4%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th><input aria-label="Выбрать все машины" checked={allOnPage} id="check-all" onChange={toggleAll} type="checkbox" /></th>
                  {th("vehicle", "Машина")}
                  {th("plate", "Госномер")}
                  <th>Тип кузова</th>
                  {th("city", "Где сейчас")}
                  <th>Статус</th>
                  {th("driver", "Водитель")}
                  {th("rate", "Ставка от")}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr className={v.vehicle_id === vehicleId ? "selected" : ""} key={v.vehicle_id}>
                    <td>
                      <input
                        aria-label={`Выбрать ${title(v)}`}
                        checked={checked.includes(v.vehicle_id)}
                        className="row-check"
                        onChange={() => setChecked((c) => (c.includes(v.vehicle_id) ? c.filter((x) => x !== v.vehicle_id) : [...c, v.vehicle_id]))}
                        type="checkbox"
                      />
                    </td>
                    <td data-label="Машина">
                      <button className="vehicle" data-action="vehicle" onClick={() => openVehicle(v.vehicle_id)} type="button">
                        <VehiclePhoto v={v} />
                        <span>
                          <strong>{title(v)}</strong>
                          <small>{v.body_type} • {v.max_load_weight != null ? `${v.max_load_weight} т` : "—"}</small>
                        </span>
                      </button>
                    </td>
                    <td data-label="Госномер"><span className="plate">{v.plate}</span></td>
                    <td data-label="Тип кузова">{v.body_type}<small>{v.max_load_weight != null ? `${v.max_load_weight} тонн` : ""}</small></td>
                    <td data-label="Где сейчас">{v.city ? <span className="city"><Ic n="pin" />{v.city}</span> : "—"}</td>
                    <td data-label="Статус">
                      <StatusPill as="button" data-id={v.vehicle_id} meta={STATUS_META} onClick={() => openVehicle(v.vehicle_id)} state={v.state} type="button" />
                    </td>
                    <td data-label="Водитель">
                      {v.driver_name ? (
                        <div className="driver">
                          <Ic n="user" />
                          <span className="driver-details">
                            {shortName(v.driver_name)}
                            {v.driver_phone && <small>{v.driver_phone}</small>}
                          </span>
                        </div>
                      ) : "—"}
                    </td>
                    <td data-label="Ставка от">{v.min_price != null ? <b>от {Number(v.min_price).toLocaleString("ru-RU")} {currencySign(v.currency)}</b> : "—"}</td>
                    <td>
                      <button aria-label={`Изменить машину ${title(v)}`} className="button row-more" data-action="vehicle" onClick={() => openVehicle(v.vehicle_id)} title="Открыть и изменить машину" type="button">···</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!loading && rows.length === 0 && (
              <div className="empty">
                <h3>{vehicles.length === 0 ? "В автопарке пока нет машин" : "Машины не найдены"}</h3>
                <p>{vehicles.length === 0 ? "Добавьте первую машину — данные и доступность заполняются в одной форме." : "Измените фильтры поиска."}</p>
                {vehicles.length === 0 ? (
                  <button className="button primary" onClick={() => navigate("/my-fleet/add?new=1")} type="button">Добавить первую машину</button>
                ) : (
                  <button className="button" data-action="reset" onClick={resetFilters} type="button">Сбросить</button>
                )}
              </div>
            )}
          </div>

          <footer className="table-footer">
            <span id="fleet-count">Показано {rows.length} из {filtered.length} {plural(filtered.length, "машины", "машин", "машин")}</span>
            <div>
              <select aria-label="Количество машин на странице" id="page-size" onChange={(e) => { setPageSize(+e.target.value); setPage(1); }} value={pageSize}>
                <option value={10}>10 на странице</option>
                <option value={20}>20 на странице</option>
              </select>
              <button aria-label="Предыдущая страница" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} type="button">‹</button>
              {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                <button className={p === curPage ? "active" : ""} key={p} onClick={() => setPage(p)} type="button">{p}</button>
              ))}
              <button aria-label="Следующая страница" disabled={curPage >= pages} onClick={() => setPage(curPage + 1)} type="button">›</button>
            </div>
          </footer>
        </main>

        <aside aria-label="Редактировать машину" className="edit-panel" ref={panelRef}>
          <header>
            <h2>Редактировать машину</h2>
            <button aria-label="Закрыть" className="icon-button" data-action="close-panel" onClick={() => setPanelOpen(false)} type="button"><Ic n="close" /></button>
          </header>
          {notFound || (!loading && !row) ? (
            <div className="drawer-empty" style={{ padding: "35px 10px", color: "#7185a6", textAlign: "center" }}>
              <h3 style={{ color: "#18345d", marginBottom: 10 }}>{detail && !notFound ? "Нет доступа к этой машине" : "Машина не найдена"}</h3>
              <p>{detail && !notFound ? "Редактировать можно только машины своего автопарка." : "Выберите машину из списка слева."}</p>
            </div>
          ) : !detail || !mine ? (
            <p style={{ padding: 20, color: "#7185a6" }}>Загрузка…</p>
          ) : (
            <>
              <div className="drawer-vehicle">
                <VehiclePhoto className="drawer-truck" v={{ ...row, photo: files.tractor_photo || row.photo }} />
                <div>
                  <strong id="drawer-model">{title(row)}</strong>
                  <small id="drawer-body">{row.body_type} • {row.max_load_weight != null ? `${row.max_load_weight} тонн` : "—"}</small>
                </div>
                <span className="plate" id="drawer-plate">{row.plate}</span>
              </div>
              <form id="edit-form" onSubmit={save}>
                <div className="edit-grid">
                  <label className="edit-field">
                    <span>Марка <b>*</b></span>
                    <input name="brand" onChange={setField("mark")} required type="text" value={form.mark} />
                  </label>
                  <label className="edit-field">
                    <span>Модель</span>
                    <input name="model" onChange={setField("model")} type="text" value={form.model} />
                  </label>
                  <label className="edit-field">
                    <span>Госномер <b>*</b></span>
                    <input name="plate" onChange={setField("plate")} required type="text" value={form.plate} />
                  </label>
                  <label className="edit-field">
                    <span>VIN</span>
                    <input maxLength={17} name="vin" onChange={setField("vin")} ref={vinRef} type="text" value={form.vin} />
                  </label>
                  <label className="edit-field">
                    <span>Тип кузова</span>
                    <span className="edit-select">
                      <select name="body" onChange={setField("body")} value={form.body}>
                        <option value="">—</option>
                        {BODY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <Ic n="chevron" />
                    </span>
                  </label>
                  <label className="edit-field">
                    <span>Грузоподъёмность, т</span>
                    <input min="0" name="capacity" onChange={setField("capacity")} step="any" type="number" value={form.capacity} />
                  </label>
                  <label className="edit-field">
                    <span>Объём, м³</span>
                    <input min="0" name="volume" onChange={setField("volume")} step="any" type="number" value={form.volume} />
                  </label>
                  <label className="edit-field">
                    <span>Город регистрации</span>
                    <input name="city" onChange={setField("registration_city")} type="text" value={form.registration_city} />
                  </label>
                </div>
                <label className="edit-field">
                  <span>Водитель</span>
                  <input
                    list="driver-list"
                    name="driver"
                    onChange={(e) => {
                      const name = e.target.value;
                      // wireframe: choosing a driver fills in his phone
                      const known = vehicles.find((v) => v.driver_name === name && v.driver_phone);
                      setForm((p) => ({ ...p, driver_name: name, driver_phone: known && !p.driver_phone ? known.driver_phone : p.driver_phone }));
                    }}
                    type="text"
                    value={form.driver_name}
                  />
                  <datalist id="driver-list">{driverOptions.map((d) => <option key={d} value={d} />)}</datalist>
                </label>
                <label className="edit-field">
                  <span>Контактный номер</span>
                  <input name="phone" onChange={setField("driver_phone")} type="tel" value={form.driver_phone} />
                </label>
                <label className="edit-field comment">
                  <span>Комментарий</span>
                  <textarea
                    maxLength={500}
                    name="comment"
                    onChange={setField("comment")}
                    placeholder="Например, особенности машины, дополнительное оборудование, условия работы..."
                    value={form.comment}
                  />
                  <small id="comment-count">{counter}</small>
                </label>

                <section className="vehicle-documents">
                  <h3>Документы</h3>
                  {DOCS.map((d) => (
                    <button data-action="document" data-doc={d.key} key={d.key} onClick={() => openDoc(d)} style={docOk(d) ? undefined : { background: "#f3f6fb" }} type="button">
                      <Ic d={ICONS.doc} />
                      <span>{d.title}</span>
                      {docOk(d) ? <b>✓</b> : <b style={{ background: "#9fb2d0" }}>+</b>}
                      <em style={docOk(d) ? undefined : { color: "#6b83ac" }}>{docState(d)}</em>
                      <i style={docOk(d) ? undefined : { color: "#6b83ac" }}>›</i>
                    </button>
                  ))}
                  <input
                    accept=".jpg,.jpeg,.png,image/*"
                    hidden
                    id="document-input"
                    onChange={(e) => {
                      pickFile(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                    ref={fileRef}
                    type="file"
                  />
                </section>

                {err && <p role="alert" style={{ margin: "10px 0 0", padding: "8px 10px", borderRadius: 8, background: "#fff1f3", color: "#d6203b", fontSize: 12 }}>{err}</p>}
                <footer>
                  <button className="button" data-action="cancel" onClick={cancel} type="button">Отмена</button>
                  <button className="button primary" disabled={busy} type="submit">{busy ? "Сохраняем…" : "Сохранить изменения"}</button>
                </footer>
              </form>
            </>
          )}
        </aside>
      </div>
      <MobileNav />
      {dd && (
        <Dialog onClose={() => setDlg(null)} title={dd.title}>
          {files[dd.field] ? (
            <>
              <p>Выбран файл: {files[`${dd.field}__name`]}</p>
              <p className="demo-note">Файл будет загружен на сервер после нажатия «Сохранить изменения».</p>
              <button className="button primary" onClick={chooseDoc} type="button">Выбрать другой файл</button>
            </>
          ) : detail?.[dd.field] ? (
            <>
              <p>Документ загружен.{" "}
                <a href={detail[dd.field]} rel="noreferrer" style={{ color: "#0063ff" }} target="_blank">Открыть</a>
              </p>
              <button className="button primary" onClick={chooseDoc} type="button">Выбрать другой файл</button>
            </>
          ) : (
            <>
              <p>Документ ещё не загружен.</p>
              <button className="button primary" onClick={chooseDoc} type="button">Выбрать файл</button>
              <p className="demo-note">Поддерживаются изображения JPG / PNG до 8 МБ.</p>
            </>
          )}
        </Dialog>
      )}
      {toastNode}
    </CabPage>
  );
}
